import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { COVER_DOC_LIMITS, COVER_EXPORT_PX, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { KIT_FONT_FACES } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../common/types';
import { kitFontName } from '../../music24/promote/themes/kitWebFonts';
import { coverEditorMessages } from '../coverEditorMessages';
import type { CoverEditorDispatch } from '../doc/useCoverEditorDoc';
import { newCoverItemId } from '../doc/coverIds';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FloatingToolbarProps {
  box: CoverTextBox;
  side: number;
  /** The height of the box on the stage in CSS pixels. */
  boxHeight: number;
  dispatch: CoverEditorDispatch;
  onOpenText: () => void;
}

/** One step of the size buttons: 30 px of the 3000 px cover. */
const SIZE_STEP = 30 / COVER_EXPORT_PX;
const NEXT_CASE = { none: 'upper', upper: 'lower', lower: 'none' } as const;
/** The toolbar goes under the box when the box is this near the top of the cover (CSS pixels). */
const TOP_ROOM = 96;

/**
 * The small toolbar at the selected text box (design cover-editor b1): the font (opens the Text
 * inspector), the size in pixels of the 3000 px cover, bold, the case, a copy and Delete.
 */
const FloatingToolbarComponent: React.FC<FloatingToolbarProps> = ({ box, side, boxHeight, dispatch, onOpenText }) => {
  const intl = useIntl();
  const face = KIT_FONT_FACES[box.fontId];
  const [minWeight, maxWeight] = face.weights;
  const isBold = box.weight >= 700;
  const patch = useCallback(
    (change: Partial<CoverTextBox>) => dispatch({ type: 'updateText', id: box.id, patch: change }),
    [box.id, dispatch]
  );
  const resize = useCallback(
    (steps: number) => {
      const [min, max] = COVER_DOC_LIMITS.sizeShare;
      patch({ sizeShare: Math.min(max, Math.max(min, box.sizeShare + steps * SIZE_STEP)) });
    },
    [box.sizeShare, patch]
  );
  const smaller = useCallback(() => resize(-1), [resize]);
  const larger = useCallback(() => resize(1), [resize]);
  const toggleBold = useCallback(
    () => patch({ weight: isBold ? Math.max(minWeight, Math.min(maxWeight, 400)) : Math.min(maxWeight, 800) }),
    [isBold, maxWeight, minWeight, patch]
  );
  const nextCase = useCallback(() => patch({ textCase: NEXT_CASE[box.textCase] }), [box.textCase, patch]);
  const copy = useCallback(
    () => dispatch({ type: 'duplicateText', id: box.id, newId: newCoverItemId('t') }),
    [box.id, dispatch]
  );
  const remove = useCallback(() => dispatch({ type: 'removeText', id: box.id }), [box.id, dispatch]);

  const top = box.y * side - boxHeight / 2;
  const isBelow = top < TOP_ROOM;
  return (
    <div
      className={styles.floatBar}
      style={{
        left: Math.min(side - 8, Math.max(8, box.x * side)),
        top: isBelow ? box.y * side + boxHeight / 2 + 20 : top - 58,
      }}
      role="toolbar"
      aria-label={intl.formatMessage(coverEditorMessages.toolbarLabel)}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button type="button" className={styles.floatItem} onClick={onOpenText}>
        {kitFontName(box.fontId)}
      </button>
      <button
        type="button"
        className={styles.floatItem}
        onClick={smaller}
        aria-label={intl.formatMessage(coverEditorMessages.toolbarSmaller)}
      >
        −
      </button>
      <span className={styles.floatValue}>{Math.round(box.sizeShare * COVER_EXPORT_PX)}</span>
      <button
        type="button"
        className={styles.floatItem}
        onClick={larger}
        aria-label={intl.formatMessage(coverEditorMessages.toolbarLarger)}
      >
        +
      </button>
      {minWeight !== maxWeight && (
        <button type="button" className={styles.floatItem} onClick={toggleBold} aria-pressed={isBold}>
          {intl.formatMessage(coverEditorMessages.toolbarBold)}
        </button>
      )}
      <button
        type="button"
        className={styles.floatItem}
        onClick={nextCase}
        aria-label={intl.formatMessage(coverEditorMessages.toolbarCase)}
      >
        Aa
      </button>
      <button type="button" className={styles.floatItem} onClick={copy}>
        {intl.formatMessage(coverEditorMessages.toolbarCopy)}
      </button>
      <button type="button" className={`${styles.floatItem} ${styles.floatDanger}`} onClick={remove}>
        {intl.formatMessage(coverEditorMessages.toolbarDelete)}
      </button>
    </div>
  );
};

export const FloatingToolbar = TypedMemo(FloatingToolbarComponent);
